import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type Team, type Season, type Game } from '../services/api';
import { locationService } from '../services/location';
import { appState } from '../store/AppState';
import './shared/TeamPicker';

type FormStep = 'initial' | 'setup' | 'details';

@customElement('game-form')
export class GameForm extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Boolean })
  editMode = false;

  @property({ type: Object })
  editGame: Game | null = null;

  @state()
  private editPopulated = false;

  @state()
  private myTeams: Team[] = [];

  @state()
  private allTeams: Team[] = [];

  @state()
  private seasons: Season[] = [];

  @state()
  private name = '';

  @state()
  private location = '';

  @state()
  private latitude: number | null = null;

  @state()
  private longitude: number | null = null;

  @state()
  private homeTeamId = '';

  @state()
  private awayTeamId = '';

  @state()
  private seasonId = '';

  @state()
  private gameDate = '';

  @state()
  private gameDateTouched = false;

  @state()
  private loading = false;

  @state()
  private locating = false;

  @state()
  private step: FormStep = 'initial';

  @state()
  private showDetails = false;

  @state()
  private isHome = true;

  @state()
  private formErrors: Map<string, string> = new Map();

  private dateUpdateInterval: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
    if (!this.editMode) {
      this.setDefaultDate();
      this.startDateSync();
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('editGame') && this.editGame && !this.editPopulated) {
      this.name = this.editGame.name || '';
      this.location = this.editGame.location || '';
      this.homeTeamId = this.editGame.home_team_id || '';
      this.awayTeamId = this.editGame.away_team_id || '';
      this.seasonId = this.editGame.season_id || '';
      this.gameDate = this.editGame.game_date?.slice(0, 16) || '';
      this.step = 'setup';
      this.editPopulated = true;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopDateSync();
  }

  private startDateSync() {
    this.stopDateSync();
    // The default date is rendered with minute precision, so polling every
    // second was wasted work — every tick triggered a reactive update with
    // an identical (or near-identical) value. 30s is enough granularity for
    // the auto-default while keeping the form quiet.
    this.dateUpdateInterval = window.setInterval(() => {
      if (!this.gameDateTouched) {
        this.setDefaultDate();
      }
    }, 30000);
  }

  private stopDateSync() {
    if (this.dateUpdateInterval !== null) {
      window.clearInterval(this.dateUpdateInterval);
      this.dateUpdateInterval = null;
    }
  }

  private async loadData() {
    try {
      const [teams, seasons] = await Promise.all([
        api.getTeams(),
        api.getSeasons(),
      ]);
      // Always sort teams alphabetically — saves admins scrolling through 200+ unsorted entries.
      const fetchedTeams = (teams || []).slice().sort((a, b) => a.name.localeCompare(b.name));
      this.allTeams = fetchedTeams;

      // For coaches (non-admin), only show teams they're assigned to.
      // Admins can see and create games for any team.
      const userTeamIds = appState.userTeamIds;
      if (appState.isAdmin) {
        this.myTeams = fetchedTeams;
      } else {
        this.myTeams = fetchedTeams.filter(t => userTeamIds.includes(t.id));
      }

      this.seasons = seasons || [];

      // Auto-select the default season for new games (not edit mode) when
      // nothing has been chosen yet. Coaches/admins can still override.
      if (!this.editMode && !this.seasonId) {
        const defaultSeason = this.seasons.find(s => s.is_default);
        if (defaultSeason) {
          this.seasonId = defaultSeason.id;
        }
      }

      // Auto-select home team if the user has at least one assigned team.
      // Applies to both coaches and admins (admins-with-a-team almost always
      // want their own team pre-selected; they can change it if needed).
      if (userTeamIds.length >= 1 && !this.homeTeamId) {
        this.homeTeamId = userTeamIds[0];
      }
    } catch (error) {
      console.error('Failed to load form data:', error);
    }
  }

  private setDefaultDate() {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    const localISOTime = new Date(now.getTime() - offset).toISOString().slice(0, 16);
    this.gameDate = localISOTime;
  }

  private getUserDisplayName(): string | undefined {
    return appState.user?.display_name;
  }

  private async detectLocation() {
    this.locating = true;
    try {
      const result = await locationService.getCurrentLocation();
      if (result) {
        this.latitude = result.latitude;
        this.longitude = result.longitude;
        if (result.name) {
          this.location = result.name;
        }
      }
      // Generate game name based on teams and location
      this.updateGameName();
    } finally {
      this.locating = false;
    }
  }

  private async handleStartSetup() {
    this.step = 'setup';
    // Start location detection in background
    this.detectLocation();
  }

  private handleBack() {
    if (this.showDetails) {
      this.showDetails = false;
    } else if (this.editMode) {
      this.dispatchEvent(new CustomEvent('edit-cancelled'));
    } else {
      this.step = 'initial';
    }
  }

  private validateForm(): boolean {
    const errors = new Map<string, string>();
    if (!this.homeTeamId) errors.set('homeTeam', 'Please select your team');
    if (!this.awayTeamId) errors.set('awayTeam', 'Please select an opponent');
    if (!this.gameDate) errors.set('gameDate', 'Please set a date and time');
    this.formErrors = errors;
    return errors.size === 0;
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    if (this.loading) return;
    if (!this.validateForm()) return;

    this.loading = true;
    try {
      // Swap team IDs if user's team is away
      const homeId = this.isHome ? this.homeTeamId : this.awayTeamId;
      const awayId = this.isHome ? this.awayTeamId : this.homeTeamId;

      const payload = {
        name: this.name || locationService.generateGameName(this.location, this.getUserDisplayName()),
        location: this.location || undefined,
        latitude: this.latitude || undefined,
        longitude: this.longitude || undefined,
        home_team_id: homeId || undefined,
        away_team_id: awayId || undefined,
        season_id: this.seasonId || undefined,
        game_date: this.gameDate,
      };

      if (this.editMode && this.editGame) {
        const game = await api.updateGame(this.editGame.id, payload);
        this.dispatchEvent(new CustomEvent<Game>('game-updated', { detail: game }));
      } else {
        const game = await api.createGame(payload);
        this.dispatchEvent(new CustomEvent<Game>('game-created', { detail: game }));
        this.resetForm();
      }
    } catch (error) {
      console.error('Failed to create game:', error);
    } finally {
      this.loading = false;
    }
  }

  private resetForm() {
    this.name = '';
    this.location = '';
    this.latitude = null;
    this.longitude = null;
    this.homeTeamId = '';
    this.awayTeamId = '';
    this.seasonId = '';
    this.setDefaultDate();
    this.gameDateTouched = false;
    this.step = 'initial';
    this.showDetails = false;
    this.formErrors = new Map();
    this.isHome = true;
    this.startDateSync();

    // Re-auto-select home team if applicable
    const userTeamIds = appState.userTeamIds;
    if (!appState.isAdmin && userTeamIds.length === 1) {
      this.homeTeamId = userTeamIds[0];
    }
  }

  private getSelectedTeamName(): string {
    const team = this.myTeams.find(t => t.id === this.homeTeamId);
    return team?.name || 'Your Team';
  }

  private getOpponentName(): string {
    if (!this.awayTeamId) return '';
    const team = this.allTeams.find(t => t.id === this.awayTeamId);
    return team?.name || '';
  }

  private getGameSummary(): string {
    const home = this.getSelectedTeamName();
    const away = this.getOpponentName();
    if (away) {
      return `${home} vs ${away}`;
    }
    return home;
  }

  private generateGameName(): string {
    const myTeam = this.getSelectedTeamName();
    const opponent = this.getOpponentName();
    const loc = this.location;

    const parts: string[] = [];

    // Add teams respecting home/away order
    if (opponent) {
      const home = this.isHome ? myTeam : opponent;
      const away = this.isHome ? opponent : myTeam;
      parts.push(`${home} vs ${away}`);
    } else if (this.homeTeamId) {
      parts.push(myTeam);
    }
    
    // Add location if available
    if (loc) {
      parts.push(loc);
    }
    
    return parts.join(' @ ') || '';
  }

  private updateGameName() {
    // Only auto-update if user hasn't manually edited the name
    const newName = this.generateGameName();
    if (newName) {
      this.name = newName;
    }
  }

  render() {
    return html`
      <div class="card bg-base-100 shadow-sm overflow-hidden">
        ${this.editMode || this.step !== 'initial' ? this.renderSetup() : this.renderInitial()}
      </div>
    `;
  }

  private renderInitial() {
    return html`
      <button
        type="button"
        class="w-full p-6 text-left group transition-all duration-200 hover:bg-base-50 active:bg-base-100"
        @click=${this.handleStartSetup}
      >
        <div class="flex items-center gap-4">
          <div class="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-105 transition-all duration-200">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-base-content group-hover:text-primary transition-colors">New Game</h3>
            <p class="text-sm text-base-content/60">Start tracking pitches</p>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-base-content/30 group-hover:text-primary/60 group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
    `;
  }

  private renderSetup() {
    // Show helpful message if user has no teams assigned
    if (this.myTeams.length === 0 && !appState.isAdmin) {
      return html`
        <div class="animate-fade-in">
          <!-- Header with back button -->
          <div class="flex items-center gap-3 p-4 border-b border-base-200">
            <button
              type="button"
              class="btn btn-ghost btn-sm btn-circle"
              @click=${this.handleBack}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 class="font-semibold">${this.editMode ? 'Edit Game' : 'New Game'}</h3>
          </div>

          <div class="p-6 text-center space-y-4">
            <div class="w-16 h-16 mx-auto rounded-full bg-base-200 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
              </svg>
            </div>
            <div>
              <h4 class="font-semibold text-lg">No Team Assigned</h4>
              <p class="text-sm text-base-content/60 mt-1">
                You need to be assigned as a coach to create games.
              </p>
            </div>
            <div class="bg-base-200 rounded-lg p-4 text-left">
              <p class="text-sm font-medium mb-2">Want to track pitches?</p>
              <ul class="text-sm text-base-content/70 space-y-1">
                <li class="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mt-0.5 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                  </svg>
                  <span>Ask a coach to share a <strong>game link</strong> with you</span>
                </li>
                <li class="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mt-0.5 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                  </svg>
                  <span>Ask your league admin for an <strong>invite</strong> to join a team</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <form @submit=${this.handleSubmit} class="animate-fade-in">
        <!-- Header with back button -->
        <div class="flex items-center gap-3 p-4 border-b border-base-200">
          <button
            type="button"
            class="btn btn-ghost btn-sm btn-circle"
            @click=${this.handleBack}
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 class="font-semibold">${this.editMode ? 'Edit Game' : 'New Game'}</h3>
        </div>

        <div class="p-4 space-y-4">
          <!-- Combined Team & Home/Away Selection -->
          <div class="space-y-3">
            <!-- Matchup Card with integrated home/away -->
            <div class="relative bg-gradient-to-b from-base-200/30 via-base-200/60 to-base-200 rounded-2xl p-3 sm:p-4 overflow-hidden">
              <!-- Baseball diamond background -->
              <div class="absolute inset-0 pointer-events-none overflow-hidden">
                <!-- Grass texture gradient -->
                <div class="absolute inset-0 bg-gradient-to-b from-success/[0.02] to-transparent"></div>
                <!-- Diamond field -->
                <svg viewBox="0 0 200 200" class="absolute -bottom-8 left-1/2 -translate-x-1/2 w-48 h-48 opacity-[0.06]" preserveAspectRatio="xMidYMid meet">
                  <!-- Outfield arc -->
                  <path d="M20 180 Q100 40 180 180" fill="none" stroke="currentColor" stroke-width="2"/>
                  <!-- Infield diamond -->
                  <path d="M100 60 L140 100 L100 140 L60 100 Z" fill="none" stroke="currentColor" stroke-width="2.5"/>
                  <!-- Base paths -->
                  <circle cx="100" cy="60" r="4" fill="currentColor"/> <!-- 2nd base -->
                  <circle cx="140" cy="100" r="4" fill="currentColor"/> <!-- 1st base -->
                  <circle cx="60" cy="100" r="4" fill="currentColor"/> <!-- 3rd base -->
                  <circle cx="100" cy="140" r="5" fill="currentColor"/> <!-- Home plate -->
                  <!-- Pitcher's mound -->
                  <circle cx="100" cy="100" r="3" fill="currentColor" opacity="0.6"/>
                </svg>
                <!-- Field lines radiating from home -->
                <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-24">
                  <div class="absolute bottom-0 left-1/2 w-px h-full bg-gradient-to-t from-base-content/[0.04] to-transparent origin-bottom -rotate-45"></div>
                  <div class="absolute bottom-0 left-1/2 w-px h-full bg-gradient-to-t from-base-content/[0.04] to-transparent origin-bottom rotate-45"></div>
                </div>
              </div>
              
              <div class="relative space-y-2 sm:space-y-3">
                <!-- Team matchup display -->
                <div class="flex items-center gap-2 sm:gap-3">
                  <!-- Home team side -->
                  <button
                    type="button"
                    class="flex-1 min-w-0 group relative rounded-xl p-2.5 sm:p-3 transition-all duration-300 touch-manipulation ${this.isHome 
                      ? 'bg-primary text-primary-content shadow-lg shadow-primary/20 scale-[1.02]' 
                      : 'bg-base-100 hover:bg-base-100/80 border-2 border-dashed border-base-300 hover:border-primary/30'}"
                    @click=${() => { this.isHome = true; this.updateGameName(); }}
                  >
                    <div class="text-[10px] sm:text-xs font-medium uppercase tracking-wider mb-0.5 sm:mb-1 ${this.isHome ? 'text-primary-content/70' : 'text-base-content/50'}">
                      Home
                    </div>
                    <div class="text-sm sm:text-base font-semibold truncate ${this.isHome ? '' : 'text-base-content/70'}">
                      ${this.isHome 
                        ? (this.getSelectedTeamName() || 'Your Team')
                        : (this.getOpponentName() || 'Opponent')}
                    </div>
                    <!-- Home indicator icon -->
                    <div class="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full ${this.isHome ? 'bg-primary-content/20' : 'bg-base-300'} flex items-center justify-center transition-all duration-300">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-3.5 sm:w-3.5 ${this.isHome ? 'text-primary-content' : 'text-base-content/40'}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                      </svg>
                    </div>
                  </button>

                  <!-- VS divider with swap functionality -->
                  <button
                    type="button"
                    class="flex-none w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-base-100 border-2 border-base-300 flex items-center justify-center hover:border-primary hover:bg-primary/5 active:scale-95 transition-all duration-200 group touch-manipulation"
                    @click=${() => { this.isHome = !this.isHome; this.updateGameName(); }}
                    title="Swap home/away"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4 text-base-content/60 group-hover:text-primary transition-all duration-200 group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                    </svg>
                  </button>

                  <!-- Away team side -->
                  <button
                    type="button"
                    class="flex-1 min-w-0 group relative rounded-xl p-2.5 sm:p-3 transition-all duration-300 touch-manipulation ${!this.isHome 
                      ? 'bg-primary text-primary-content shadow-lg shadow-primary/20 scale-[1.02]' 
                      : 'bg-base-100 hover:bg-base-100/80 border-2 border-dashed border-base-300 hover:border-primary/30'}"
                    @click=${() => { this.isHome = false; this.updateGameName(); }}
                  >
                    <div class="text-[10px] sm:text-xs font-medium uppercase tracking-wider mb-0.5 sm:mb-1 ${!this.isHome ? 'text-primary-content/70' : 'text-base-content/50'}">
                      Away
                    </div>
                    <div class="text-sm sm:text-base font-semibold truncate ${!this.isHome ? '' : 'text-base-content/70'}">
                      ${!this.isHome 
                        ? (this.getSelectedTeamName() || 'Your Team')
                        : (this.getOpponentName() || 'Opponent')}
                    </div>
                    <!-- Away indicator icon -->
                    <div class="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full ${!this.isHome ? 'bg-primary-content/20' : 'bg-base-300'} flex items-center justify-center transition-all duration-300">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-3.5 sm:w-3.5 ${!this.isHome ? 'text-primary-content' : 'text-base-content/40'}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                      </svg>
                    </div>
                  </button>
                </div>

                <!-- Team selectors - order follows home/away position -->
                <div class="grid grid-cols-2 gap-2 sm:gap-3">
                  ${this.myTeams.length > 1 ? html`
                    <!-- Home side picker -->
                    <div class="form-control animate-fade-in transition-all duration-300 ${this.isHome ? 'order-1' : 'order-2'}">
                      <team-picker
                        .teams=${this.myTeams}
                        .value=${this.homeTeamId}
                        placeholder="Your team..."
                        ?error=${this.formErrors.has('homeTeam')}
                        ?highlight=${this.isHome}
                        @team-change=${(e: CustomEvent<{ id: string }>) => { this.homeTeamId = e.detail.id; this.formErrors.delete('homeTeam'); this.updateGameName(); }}
                      ></team-picker>
                      ${this.formErrors.has('homeTeam') ? html`<label class="label py-0.5"><span class="label-text-alt text-error">${this.formErrors.get('homeTeam')}</span></label>` : nothing}
                    </div>

                    <!-- Away side picker -->
                    <div class="form-control animate-fade-in transition-all duration-300 ${this.isHome ? 'order-2' : 'order-1'}">
                      <team-picker
                        .teams=${this.allTeams.filter(t => t.id !== this.homeTeamId)}
                        .value=${this.awayTeamId}
                        placeholder="Opponent..."
                        ?error=${this.formErrors.has('awayTeam')}
                        @team-change=${(e: CustomEvent<{ id: string }>) => { this.awayTeamId = e.detail.id; this.formErrors.delete('awayTeam'); this.updateGameName(); }}
                      ></team-picker>
                      ${this.formErrors.has('awayTeam') ? html`<label class="label py-0.5"><span class="label-text-alt text-error">${this.formErrors.get('awayTeam')}</span></label>` : nothing}
                    </div>
                  ` : html`
                    <!-- Single team: just opponent picker, full width -->
                    <div class="form-control animate-fade-in col-span-2">
                      <team-picker
                        .teams=${this.allTeams.filter(t => t.id !== this.homeTeamId)}
                        .value=${this.awayTeamId}
                        placeholder="Select opponent..."
                        ?error=${this.formErrors.has('awayTeam')}
                        @team-change=${(e: CustomEvent<{ id: string }>) => { this.awayTeamId = e.detail.id; this.formErrors.delete('awayTeam'); this.updateGameName(); }}
                      ></team-picker>
                      ${this.formErrors.has('awayTeam') ? html`<label class="label py-0.5"><span class="label-text-alt text-error">${this.formErrors.get('awayTeam')}</span></label>` : nothing}
                    </div>
                  `}
                </div>

                <!-- Helper text -->
                <p class="text-xs text-base-content/40 text-center">
                  ${this.isHome ? '🏠 Home team records pitches' : '🚌 Away team — opponent records'}
                </p>
              </div>
            </div>
            ${this.formErrors.has('homeTeam') && this.myTeams.length <= 1 ? html`<label class="label py-0.5"><span class="label-text-alt text-error">${this.formErrors.get('homeTeam')}</span></label>` : nothing}
          </div>

          <!-- Location indicator -->
          <div class="flex items-center gap-2 px-1 text-sm ${this.locating ? 'animate-pulse' : ''}">
            ${this.locating ? html`
              <span class="loading loading-spinner loading-xs text-primary"></span>
              <span class="text-base-content/60">Finding location...</span>
            ` : this.location ? html`
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="text-base-content/70 truncate">${this.location}</span>
            ` : html`
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="text-base-content/50">Location unavailable</span>
            `}
          </div>

          <!-- More options toggle -->
          <button
            type="button"
            class="btn btn-ghost btn-xs gap-1 text-base-content/60 hover:text-base-content"
            @click=${() => this.showDetails = !this.showDetails}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              class="h-4 w-4 transition-transform duration-200 ${this.showDetails ? 'rotate-180' : ''}" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            ${this.showDetails ? 'Less options' : 'More options'}
          </button>

          <!-- Expandable details -->
          ${this.showDetails ? html`
            <div class="space-y-3 pt-2 animate-fade-in border-t border-base-200">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-sm">Game Name</span>
                </label>
                <input
                  type="text"
                  class="input input-bordered"
                  placeholder="Auto-generated"
                  .value=${this.name}
                  @input=${(e: Event) => this.name = (e.target as HTMLInputElement).value}
                />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-sm">Location</span>
                </label>
                <input
                  type="text"
                  class="input input-bordered"
                  placeholder="Field name"
                  .value=${this.location}
                  @input=${(e: Event) => this.location = (e.target as HTMLInputElement).value}
                />
              </div>

              <div class="grid grid-cols-1 ${this.seasons.length > 1 ? 'sm:grid-cols-2' : ''} gap-3">
                ${this.seasons.length > 1 ? html`
                  <div class="form-control">
                    <label class="label py-1">
                      <span class="label-text text-sm">Season</span>
                    </label>
                    <select
                      class="select select-bordered"
                      .value=${this.seasonId}
                      @change=${(e: Event) => this.seasonId = (e.target as HTMLSelectElement).value}
                    >
                      <option value="">None</option>
                      ${this.seasons.map(season => html`
                        <option value=${season.id}>${season.name}${season.is_default ? ' (default)' : ''}</option>
                      `)}
                    </select>
                  </div>
                ` : nothing}

                <div class="form-control">
                  <label class="label py-1">
                    <span class="label-text text-sm">Date & Time</span>
                  </label>
                  <input
                    type="datetime-local"
                    class="input input-bordered"
                    .value=${this.gameDate}
                    @input=${(e: Event) => {
                      this.gameDate = (e.target as HTMLInputElement).value;
                      this.gameDateTouched = true;
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          ` : nothing}
        </div>

        <!-- Submit button -->
        <div class="p-4 pt-0">
          <button
            type="submit"
            class="btn btn-primary btn-block gap-2"
            ?disabled=${this.loading || !this.homeTeamId || !this.awayTeamId}
          >
            ${this.loading ? html`
              <span class="loading loading-spinner loading-sm"></span>
            ` : html`
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            `}
            ${this.editMode ? 'Save Changes' : 'Start Game'}
          </button>
        </div>
      </form>
    `;
  }
}
